import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, updateDoc, doc, deleteDoc, onSnapshot, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { getAuth } from 'firebase/auth';
import 'bootstrap/dist/css/bootstrap.min.css';

const AffiliationRequestsManager = () => {
  // États pour les médecins
  const [pendingDoctorRequests, setPendingDoctorRequests] = useState([]);
  const [processedDoctorRequests, setProcessedDoctorRequests] = useState([]);
  const [affiliatedDoctors, setAffiliatedDoctors] = useState([]);
  
  // États pour les patients
  const [affiliationRequests, setAffiliationRequests] = useState([]);
  const [rendezVousRequests, setRendezVousRequests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [medecins, setMedecins] = useState([]);
  
  // États communs
  const [activeTab, setActiveTab] = useState('doctorAffiliations');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('en attente');
  const [filtrePatient, setFiltrePatient] = useState('');
  const [filtreMedecin, setFiltreMedecin] = useState('');
  const [structureData, setStructureData] = useState(null);
  
  // État pour détecter si l'écran est mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribers = [];

    const fetchData = async () => {
      // Vérifier que l'utilisateur est connecté et a un UID valide
      if (!auth.currentUser || !auth.currentUser.uid) {
        console.log("Aucun utilisateur connecté ou UID manquant");
        setLoading(false);
        return;
      }
      
      const userUid = auth.currentUser.uid;
  
      try {
        // Récupérer les informations de la structure
        const structureDoc = await getDoc(doc(db, 'structures', userUid));
        if (structureDoc.exists()) {
          setStructureData(structureDoc.data());
        }
        
        // Récupérer les médecins de cette structure
        const medecinsQuery = query(
          collection(db, 'medecins'),
          where("structureId", "==", userUid)
        );
        const medecinsSnapshot = await getDocs(medecinsQuery);
        const medecinsData = medecinsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMedecins(medecinsData);
        setAffiliatedDoctors(medecinsData);
  
        // Mettre en place des écouteurs pour les demandes d'affiliation des médecins
        const doctorAffiliationsQuery = query(
          collection(db, 'affiliationRequests'),
          where('structureId', '==', userUid)
        );
  
        const unsubscribeDoctorAffiliations = onSnapshot(doctorAffiliationsQuery, async (snapshot) => {
          const pending = [];
          const processed = [];
          
          for (const docSnap of snapshot.docs) {
            const requestData = docSnap.data();
            if (requestData.medecinId) {
              const medecinDoc = await getDoc(doc(db, 'medecins', requestData.medecinId));
              
              if (medecinDoc.exists()) {
                const request = {
                  id: docSnap.id,
                  ...requestData,
                  medecin: medecinDoc.data()
                };
                
                if (requestData.status === 'pending') {
                  pending.push(request);
                } else {
                  processed.push(request);
                }
              }
            }
          }
          
          setPendingDoctorRequests(pending);
          setProcessedDoctorRequests(processed);
        });
        unsubscribers.push(unsubscribeDoctorAffiliations);
  
        // Écouteurs pour les demandes d'affiliation des patients
        const patientAffiliationsQuery = query(
          collection(db, 'affiliations'),
          where('structureId', '==', userUid)
        );
  
        const unsubscribePatientAffiliations = onSnapshot(patientAffiliationsQuery, async (snapshot) => {
          const requests = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAtFormatted: data.createdAt ? 
                (data.createdAt instanceof Timestamp ? 
                  new Date(data.createdAt.toDate()).toLocaleString() : 
                  new Date(data.createdAt).toLocaleString()) 
                : 'Date inconnue'
            };
          });
          setAffiliationRequests(requests);
          
          // Récupérer les informations des patients
          const patientIds = [...new Set(requests.map(req => req.patientId).filter(id => id))];
          if (patientIds.length > 0) {
            const patientsData = [];
            for (const patientId of patientIds) {
              const patientDoc = await getDoc(doc(db, 'patients', patientId));
              if (patientDoc.exists()) {
                patientsData.push({
                  id: patientDoc.id,
                  ...patientDoc.data()
                });
              }
            }
            setPatients(patientsData);
          }
        });
        unsubscribers.push(unsubscribePatientAffiliations);
  
        // Écouteurs pour les demandes de rendez-vous
        const rendezVousQuery = query(
          collection(db, 'rendezvous'),
          where('structureId', '==', userUid)
        );
  
        const unsubscribeRendezVous = onSnapshot(rendezVousQuery, (snapshot) => {
          const requests = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAtFormatted: data.createdAt ? 
                (data.createdAt instanceof Timestamp ? 
                  new Date(data.createdAt.toDate()).toLocaleString() : 
                  new Date(data.createdAt).toLocaleString()) 
                : 'Date inconnue'
            };
          });
          setRendezVousRequests(requests);
        });
        unsubscribers.push(unsubscribeRendezVous);
  
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setMessage(`Erreur: ${error.message}`);
        setLoading(false);
      }
    };
  
    if (auth.currentUser) {
      fetchData();
    } else {
      setLoading(false);
    }
  
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [auth.currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Gestion des demandes d'affiliation des médecins
  const handleApproveDoctorRequest = async (request) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, 'affiliationRequests', request.id), {
        status: 'accepted',
        acceptedBy: user.uid,
        acceptedAt: Timestamp.now()
      });
      
      await updateDoc(doc(db, 'medecins', request.medecinId), {
        structureId: user.uid,
        structureAffiliationDate: Timestamp.now(),
        structureNom: request.structureName || '',
        isAffiliated: true
      });
      
      setMessage('Demande acceptée');
      setTimeout(() => setMessage(''), 2000);
      
      setPendingDoctorRequests(prev => prev.filter(req => req.id !== request.id));
      
      const updatedRequest = {
        ...request,
        status: 'accepted',
        acceptedBy: user.uid,
        acceptedAt: Timestamp.now()
      };
      setProcessedDoctorRequests(prev => [updatedRequest, ...prev]);
      
      const medecinDoc = await getDoc(doc(db, 'medecins', request.medecinId));
      if (medecinDoc.exists()) {
        const medecinData = medecinDoc.data();
        setAffiliatedDoctors(prev => [
          {
            id: request.medecinId,
            ...medecinData,
            structureId: user.uid,
            structureAffiliationDate: Timestamp.now(),
            isAffiliated: true
          },
          ...prev
        ]);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const handleRejectDoctorRequest = async (request) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, 'affiliationRequests', request.id), {
        status: 'rejected',
        rejectedBy: user.uid,
        rejectedAt: Timestamp.now()
      });
      
      setMessage('Demande refusée');
      setTimeout(() => setMessage(''), 2000);
      
      setPendingDoctorRequests(prev => prev.filter(req => req.id !== request.id));
      
      const updatedRequest = {
        ...request,
        status: 'rejected',
        rejectedBy: user.uid,
        rejectedAt: Timestamp.now()
      };
      setProcessedDoctorRequests(prev => [updatedRequest, ...prev]);
    } catch (error) {
      console.error('Erreur:', error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const handleRemoveDoctorAffiliation = async (doctorId) => {
    if (window.confirm('Supprimer cette affiliation ?')) {
      try {
        const user = auth.currentUser;
        if (!user) return;
        
        await updateDoc(doc(db, 'medecins', doctorId), {
          structureId: null,
          structureAffiliationDate: null,
          structureNom: '',
          isAffiliated: false
        });
        
        const affiliationsQuery = query(
          collection(db, 'affiliationRequests'),
          where("medecinId", "==", doctorId),
          where("structureId", "==", user.uid)
        );
        
        const querySnapshot = await getDocs(affiliationsQuery);
        const deletePromises = [];
        querySnapshot.forEach((doc) => {
          deletePromises.push(deleteDoc(doc.ref));
        });
        
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
        }
        
        await addDoc(collection(db, 'affiliationHistory'), {
          medecinId: doctorId,
          structureId: user.uid,
          action: 'removed',
          timestamp: Timestamp.now()
        });
        
        setMessage('Affiliation supprimée');
        setTimeout(() => setMessage(''), 2000);
        setAffiliatedDoctors(prev => prev.filter(doc => doc.id !== doctorId));
      } catch (error) {
        console.error('Erreur:', error);
        setMessage(`Erreur: ${error.message}`);
      }
    }
  };

  // Gestion des demandes d'affiliation des patients
  const handleAffiliationRequest = async (id, status) => {
    try {
      if (!auth.currentUser) return;

      await updateDoc(doc(db, 'affiliations', id), {
        statut: status,
        updatedAt: Timestamp.now()
      });
      
      if (status === 'acceptée') {
        const affiliationDoc = await getDoc(doc(db, 'affiliations', id));
        if (affiliationDoc.exists()) {
          const affiliationData = affiliationDoc.data();
          const patientId = affiliationData.patientId;
          
          if (patientId) {
            const patientDoc = await getDoc(doc(db, 'patients', patientId));
            if (patientDoc.exists()) {
              await updateDoc(doc(db, 'patients', patientId), {
                structureId: auth.currentUser.uid,
                updatedAt: Timestamp.now()
              });
              
              await addDoc(collection(db, 'notifications'), {
                userId: patientId,
                titre: "Affiliation acceptée",
                message: `Votre demande d'affiliation à ${structureData?.nom || 'notre structure'} a été acceptée.`,
                lu: false,
                createdAt: Timestamp.now()
              });
            }
          }
        }
      } else if (status === 'refusée') {
        const affiliationDoc = await getDoc(doc(db, 'affiliations', id));
        if (affiliationDoc.exists()) {
          const affiliationData = affiliationDoc.data();
          const patientId = affiliationData.patientId;
          
          if (patientId) {
            await addDoc(collection(db, 'notifications'), {
              userId: patientId,
              titre: "Affiliation refusée",
              message: `Votre demande d'affiliation à ${structureData?.nom || 'notre structure'} a été refusée.`,
              lu: false,
              createdAt: Timestamp.now()
            });
          }
        }
      }
      
      setMessage(status === 'acceptée' ? 'Demande acceptée' : 'Demande refusée');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error("Erreur:", error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  // Gestion des rendez-vous
  const handleRendezVousRequest = async (id, status) => {
    try {
      if (!auth.currentUser) return;

      await updateDoc(doc(db, 'rendezvous', id), {
        statut: status,
        updatedAt: Timestamp.now()
      });
      
      if (status === 'confirmé') {
        const rendezVousDoc = await getDoc(doc(db, 'rendezvous', id));
        if (rendezVousDoc.exists()) {
          const rendezVousData = rendezVousDoc.data();
          
          if (rendezVousData.patientId) {
            await addDoc(collection(db, 'notifications'), {
              userId: rendezVousData.patientId,
              titre: "RDV confirmé",
              message: `Votre rendez-vous du ${formatDate(rendezVousData.date)} à ${rendezVousData.heure} a été confirmé.`,
              lu: false,
              createdAt: Timestamp.now()
            });
          }
          
          if (rendezVousData.medecinId) {
            await addDoc(collection(db, 'notifications'), {
              userId: rendezVousData.medecinId,
              titre: "Nouveau RDV",
              message: `Un RDV a été confirmé pour le ${formatDate(rendezVousData.date)}.`,
              lu: false,
              createdAt: Timestamp.now()
            });
          }
        }
      } else if (status === 'annulé') {
        const rendezVousDoc = await getDoc(doc(db, 'rendezvous', id));
        if (rendezVousDoc.exists()) {
          const rendezVousData = rendezVousDoc.data();
          
          if (rendezVousData.patientId) {
            await addDoc(collection(db, 'notifications'), {
              userId: rendezVousData.patientId,
              titre: "RDV annulé",
              message: `Votre RDV pour le ${formatDate(rendezVousData.date)} a été annulé.`,
              lu: false,
              createdAt: Timestamp.now()
            });
          }
        }
      }
      
      setMessage(status === 'confirmé' ? 'RDV confirmé' : 'RDV ' + status);
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error("Erreur:", error);
      setMessage(`Erreur: ${error.message}`);
    }
  };

  // Fonctions utilitaires
  const getPatientInfo = (patientId) => {
    return patients.find(p => p.id === patientId) || { nom: 'Patient inconnu', prenom: '' };
  };

  const getMedecinInfo = (medecinId) => {
    return medecins.find(m => m.id === medecinId) || { nom: 'Médecin inconnu', prenom: '' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    } catch (e) {
      return 'Date invalide';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'accepted':
      case 'acceptée':
      case 'confirmé':
        return 'bg-success';
      case 'pending':
      case 'en attente':
        return 'bg-warning';
      case 'rejected':
      case 'refusée':
      case 'annulé':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const renderMobileView = () => {
    if (activeTab === 'doctorAffiliations') {
      return (
        <div className="mobile-view">
          <div className="section-title">
            <h2>Médecins</h2>
            <div className="tabs">
              <button 
                className={`tab ${pendingDoctorRequests.length > 0 ? 'has-badge' : ''}`}
                onClick={() => setActiveTab('doctorAffiliations')}
              >
                En attente
                {pendingDoctorRequests.length > 0 && (
                  <span className="badge">{pendingDoctorRequests.length}</span>
                )}
              </button>
              <button 
                className="tab"
                onClick={() => setActiveTab('affiliatedDoctors')}
              >
                Affiliés
              </button>
            </div>
          </div>
          
          {pendingDoctorRequests.length > 0 ? (
            <div className="cards-container">
              {pendingDoctorRequests.map(request => (
                <div className="card" key={request.id}>
                  <div className="card-header">
                    <h3>Dr. {request.medecin?.prenom || ''} {request.medecin?.nom || ''}</h3>
                    <span className="badge bg-warning">En attente</span>
                  </div>
                  <div className="card-body">
                    <p className="specialty">{request.medecin?.specialite || 'Spécialité non précisée'}</p>
                    <p className="info">{request.medecin?.email || ''}</p>
                    <p className="info">{request.medecin?.telephone || ''}</p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn-accept"
                      onClick={() => handleApproveDoctorRequest(request)}
                    >
                      <i className="bi bi-check-lg"></i>
                    </button>
                    <button 
                      className="btn-reject"
                      onClick={() => handleRejectDoctorRequest(request)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucune demande d'affiliation en attente</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'affiliatedDoctors') {
      return (
        <div className="mobile-view">
          <div className="section-title">
            <h2>Médecins</h2>
            <div className="tabs">
              <button 
                className={`tab ${pendingDoctorRequests.length > 0 ? 'has-badge' : ''}`}
                onClick={() => setActiveTab('doctorAffiliations')}
              >
                En attente
                {pendingDoctorRequests.length > 0 && (
                  <span className="badge">{pendingDoctorRequests.length}</span>
                )}
              </button>
              <button 
                className="tab active"
                onClick={() => setActiveTab('affiliatedDoctors')}
              >
                Affiliés
              </button>
            </div>
          </div>
          
          {affiliatedDoctors.length > 0 ? (
            <div className="cards-container">
              {affiliatedDoctors.map(doctor => (
                <div className="card" key={doctor.id}>
                  <div className="card-header">
                    <h3>Dr. {doctor?.prenom || ''} {doctor?.nom || ''}</h3>
                    <span className="badge bg-success">Affilié</span>
                  </div>
                  <div className="card-body">
                    <p className="specialty">{doctor?.specialite || 'Spécialité non précisée'}</p>
                    <p className="info">{doctor?.email || ''}</p>
                    <p className="info">{doctor?.telephone || ''}</p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn-delete"
                      onClick={() => handleRemoveDoctorAffiliation(doctor.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucun médecin affilié</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'patientAffiliations') {
      const filteredRequests = affiliationRequests.filter(request => {
        if (filtreStatut && request.statut !== filtreStatut) return false;
        if (filtrePatient && request.patientId !== filtrePatient) return false;
        return true;
      });
      
      return (
        <div className="mobile-view">
          <div className="section-title">
            <h2>Patients</h2>
            <div className="filter">
              <select 
                className="filter-select" 
                value={filtreStatut} 
                onChange={(e) => setFiltreStatut(e.target.value)}
              >
                <option value="">Tous</option>
                <option value="en attente">En attente</option>
                <option value="acceptée">Acceptée</option>
                <option value="refusée">Refusée</option>
              </select>
            </div>
          </div>
          
          {filteredRequests.length > 0 ? (
            <div className="cards-container">
              {filteredRequests.map(request => {
                const patient = getPatientInfo(request.patientId);
                return (
                  <div className="card" key={request.id}>
                    <div className="card-header">
                      <h3>{patient.prenom} {patient.nom}</h3>
                      <span className={`badge ${getStatusBadgeClass(request.statut)}`}>
                        {request.statut.charAt(0).toUpperCase() + request.statut.slice(1)}
                      </span>
                    </div>
                    <div className="card-body">
                      <p className="info">{patient.email || ''}</p>
                      <p className="info">{patient.telephone || ''}</p>
                      <p className="date">Demande: {formatDate(request.createdAt)}</p>
                      {request.motif && <p className="motif">{request.motif}</p>}
                    </div>
                    {request.statut === 'en attente' && (
                      <div className="card-actions">
                        <button 
                          className="btn-accept"
                          onClick={() => handleAffiliationRequest(request.id, 'acceptée')}
                        >
                          <i className="bi bi-check-lg"></i>
                        </button>
                        <button 
                          className="btn-reject"
                          onClick={() => handleAffiliationRequest(request.id, 'refusée')}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucune demande d'affiliation</p>
            </div>
          )}
        </div>
      );
    } else if (activeTab === 'rendezVous') {
      const filteredRequests = rendezVousRequests.filter(request => {
        if (filtreStatut && request.statut !== filtreStatut) return false;
        if (filtrePatient && request.patientId !== filtrePatient) return false;
        if (filtreMedecin && request.medecinId !== filtreMedecin) return false;
        return true;
      });
      
      return (
        <div className="mobile-view">
          <div className="section-title">
            <h2>Rendez-vous</h2>
            <div className="filter">
              <select 
                className="filter-select" 
                value={filtreStatut} 
                onChange={(e) => setFiltreStatut(e.target.value)}
              >
                <option value="">Tous</option>
                <option value="en attente">En attente</option>
                <option value="confirmé">Confirmé</option>
                <option value="en cours">En cours</option>
                <option value="terminé">Terminé</option>
                <option value="annulé">Annulé</option>
              </select>
            </div>
          </div>
          
          {filteredRequests.length > 0 ? (
            <div className="cards-container">
              {filteredRequests.map(request => {
                const patient = getPatientInfo(request.patientId);
                const medecin = getMedecinInfo(request.medecinId);
                return (
                  <div className={`card ${request.urgent ? 'urgent' : ''}`} key={request.id}>
                    <div className="card-header">
                      <div>
                        <h3>{patient.prenom} {patient.nom}</h3>
                        <p className="doctor">Dr. {medecin.prenom} {medecin.nom}</p>
                      </div>
                      <span className={`badge ${getStatusBadgeClass(request.statut)}`}>
                        {request.statut.charAt(0).toUpperCase() + request.statut.slice(1)}
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="date-time">
                        <span className="date">{formatDate(request.date)}</span>
                        <span className="time">{request.heure}</span>
                        {request.urgent && <span className="urgent-tag">Urgent</span>}
                      </div>
                      {request.motif && <p className="motif">{request.motif}</p>}
                    </div>
                    {request.statut === 'en attente' ? (
                      <div className="card-actions">
                        <button 
                          className="btn-accept"
                          onClick={() => handleRendezVousRequest(request.id, 'confirmé')}
                        >
                          <i className="bi bi-check-lg"></i>
                        </button>
                        <button 
                          className="btn-reject"
                          onClick={() => handleRendezVousRequest(request.id, 'annulé')}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="card-actions">
                        <select 
                          className="status-select"
                          value={request.statut}
                          onChange={(e) => handleRendezVousRequest(request.id, e.target.value)}
                        >
                          <option value="confirmé">Confirmé</option>
                          <option value="en cours">En cours</option>
                          <option value="terminé">Terminé</option>
                          <option value="annulé">Annulé</option>
                          <option value="absent">Patient absent</option>
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucun rendez-vous</p>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="affiliation-manager">
      {message && (
        
        <div className="toast-message">
          <div className={`toast-content ${message.includes('Erreur') ? 'error' : 'success'}`}>
            {message}
          </div>
        </div>
      )}

      <div className="mobile-navbar">
        <button 
          className={`nav-btn ${activeTab === 'doctorAffiliations' || activeTab === 'affiliatedDoctors' ? 'active' : ''}`} 
          onClick={() => setActiveTab('doctorAffiliations')}
        >
          <i className="bi bi-person-badge"></i>
          <span>Médecins</span>
          {pendingDoctorRequests.length > 0 && (
            <span className="nav-badge">{pendingDoctorRequests.length}</span>
          )}
        </button>
        <button 
          className={`nav-btn ${activeTab === 'patientAffiliations' ? 'active' : ''}`} 
          onClick={() => setActiveTab('patientAffiliations')}
        >
          <i className="bi bi-people"></i>
          <span>Patients</span>
          {affiliationRequests.filter(req => req.statut === 'en attente').length > 0 && (
            <span className="nav-badge">{affiliationRequests.filter(req => req.statut === 'en attente').length}</span>
          )}
        </button>
        <button 
          className={`nav-btn ${activeTab === 'rendezVous' ? 'active' : ''}`} 
          onClick={() => setActiveTab('rendezVous')}
        >
          <i className="bi bi-calendar-check"></i>
          <span>RDV</span>
          {rendezVousRequests.filter(req => req.statut === 'en attente').length > 0 && (
            <span className="nav-badge">{rendezVousRequests.filter(req => req.statut === 'en attente').length}</span>
          )}
        </button>
      </div>

      <div className="mobile-content">
        {renderMobileView()}
      </div>

      <style jsx>{`
        /* Styles de base pour mobile */
        .affiliation-manager {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 0;
          margin: 0;
          position: relative;
          padding-bottom: 70px; /* Espace pour la barre de navigation */
        }

        /* Barre de navigation mobile */
        .mobile-navbar {
          display: flex;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
          z-index: 1000;
        }

        .nav-btn {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 10px 0;
          background: none;
          border: none;
          color: #6c757d;
          font-size: 12px;
          position: relative;
        }

        .nav-btn i {
          font-size: 20px;
          margin-bottom: 4px;
        }

        .nav-btn.active {
          color: #3a7bd5;
        }

        .nav-badge {
          position: absolute;
          top: 5px;
          right: calc(50% - 15px);
          background: #ff5757;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Contenu mobile */
        .mobile-content {
          padding: 15px;
          padding-bottom: 80px;
        }

        /* Titres des sections */
        .section-title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .section-title h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }

        /* Filtres */
        .filter {
          display: flex;
          align-items: center;
        }

        .filter-select {
          border: 1px solid #dee2e6;
          border-radius: 20px;
          padding: 5px 10px;
          font-size: 12px;
          background-color: white;
          color: #495057;
        }

        /* Onglets */
        .tabs {
          display: flex;
          background: #f1f3f5;
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 15px;
        }

        .tab {
          flex: 1;
          padding: 8px 12px;
          font-size: 12px;
          border: none;
          background: none;
          color: #495057;
          position: relative;
        }

        .tab.active {
          background: #3a7bd5;
          color: white;
        }

        .tab.has-badge {
          font-weight: bold;
        }

        .tab .badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #ff5757;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Cartes */
        .cards-container {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .card.urgent {
          border-left: 3px solid #ff9800;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 12px 15px;
          border-bottom: 1px solid #f1f3f5;
        }

        .card-header h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
          color: #212529;
        }

        .card-header .doctor {
          font-size: 12px;
          color: #6c757d;
          margin: 3px 0 0 0;
        }

        .badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 500;
        }

        .bg-success {
          background-color: #38b48b !important;
        }

        .bg-warning {
          background-color: #ffa41b !important;
        }

        .bg-danger {
          background-color: #ff5757 !important;
        }

        .card-body {
          padding: 12px 15px;
        }

        .specialty {
          font-size: 13px;
          color: #3a7bd5;
          font-weight: 500;
          margin: 0 0 8px 0;
        }

        .info {
          font-size: 12px;
          color: #6c757d;
          margin: 4px 0;
        }

        .date-time {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .date {
          font-size: 12px;
          color: #495057;
        }

        .time {
          font-size: 12px;
          font-weight: 600;
          color: #3a7bd5;
        }

        .urgent-tag {
          background: #fff3cd;
          color: #856404;
          padding: 2px 6px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 500;
        }

        .motif {
          font-size: 12px;
          color: #495057;
          margin: 8px 0 0 0;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .card-actions {
          display: flex;
          border-top: 1px solid #f1f3f5;
        }

        .card-actions button {
          flex: 1;
          padding: 12px;
          border: none;
          background: none;
          font-size: 16px;
        }

        .btn-accept {
          color: #38b48b;
        }

        .btn-reject {
          color: #ff5757;
        }

        .btn-delete {
          color: #6c757d;
        }

        .status-select {
          width: 100%;
          padding: 10px;
          border: none;
          background: #f8f9fa;
          color: #495057;
          font-size: 12px;
          text-align-last: center;
        }

        /* État vide */
        .empty-state {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 30px 15px;
          background: white;
          border-radius: 12px;
          color: #6c757d;
          font-size: 14px;
          text-align: center;
        }

        /* Toast de notification */
        .toast-message {
          position: fixed;
          top: 20px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          z-index: 1100;
          pointer-events: none;
        }

        .toast-content {
          padding: 10px 20px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          animation: fadeInOut 2s forwards;
        }

        .toast-content.success {
          background-color: #38b48b;
          color: white;
        }

        .toast-content.error {
          background-color: #ff5757;
          color: white;
        }

        /* Animation pour le toast */
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-20px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-20px); }
        }

        /* Écran de chargement */
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          width: 100%;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(58, 123, 213, 0.3);
          border-radius: 50%;
          border-top-color: #3a7bd5;
          animation: spin 1s ease-in-out infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AffiliationRequestsManager;
